import Vue from 'vue';
import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators';
import { ITeacherWidgetHomework } from '../interfaces/teacher-widget-homework.interface';
import { HomeworksService } from '../services/homeworks.service';
import { WidgetTeacherCourse } from '../models/widget-teacher-course.model';
import { WidgetTeacherPerson } from '../models/widget-teacher-person.model';
import { WidgetTeacherHomework } from '../models/widget-teacher-homework.model';
import { RequestCreateHomework } from '../models/request-create-homework.model';
import { MUTATIONS, PAGE_SIZE, _HOMEWORKS_STORE, HOMEWORK_STATUS, TEACHER_WIDGET_TASK_TABS, HOMEWORK_SOCKET_MESSAGES_TYPES } from '../values/homeworks.values';
import { IStudentWidgetHomework } from '../interfaces/studentWidgetHomework.interface';
import { WidgetStudentCourse } from '../models/widget-student-course.model';
import { ITeacherWidgetHomeworkSection } from '../interfaces/teacher-widget-homework-section.interface';
import { WidgetTeacherSectionList } from '../models/widget-teacher-section-list.model';
import { HomeworksToolsService } from '../services/homeworks-tools.service';
import { WidgetTeacherSectionItem } from '../models/widget-teacher-section-item.model';
import { IRequestSetDraftHomework } from '../interfaces/request-set-draft-homework.interface';
import { IRequestSetFeedbacDraft } from '../interfaces/request-set-feedback-draft.interface';
import { IWhiteboardHashUsers } from '../interfaces/whiteboard-hash-users.interface';
import { IWhiteboardUser } from '../interfaces/whiteboard-user.interface';
import { IRequestHomeworkBySectionStudent } from '../interfaces/request-homework-by-section-student.interface';
import { HomeworkStatusStudent } from '../models/homework-status-student-response.model';
import { IRequestHomeworkStatusByStudent } from '../interfaces/request-homework-status-by-student.interface';
import Moment from 'moment';
import {IOnlineHomework, IOnlineHomeworkDetail} from '../interfaces/online-homework.interface';
import { BOARD_CONFIG_PARAMETERS } from '../../values/ed-whiteboard.values';
import Content from '@/content/models/content.model';

export const HOMEWORKS_STORE = _HOMEWORKS_STORE; 

@Module({ namespaced: true })
export class HomeworksStore extends VuexModule {

  /* State */
  public teacherWidgetTabSelected: string = TEACHER_WIDGET_TASK_TABS.BY_STUDENT;

  public teacherWidgetHomeworks: ITeacherWidgetHomework = {
    firstLoad: false,
    personSelected: null,
    courseOpened: null, 
    courses: { page: 0, more: true, content: [] }
  };

  public teacherWidgetHomeworksSection: ITeacherWidgetHomeworkSection = {
    firstLoad: false,
    sectionSelected: null,
    courseOpened: null,
    courses: { page: 0, more: true, content: [], pageSize: PAGE_SIZE }
  };

  public studentWidgetHomeworks: IStudentWidgetHomework = {
    firstLoad: false, courses: []
  }

  public confirmCloseHomeworkViewer: boolean = false;

  public boardHomeworkHashUsers : IWhiteboardHashUsers[] = [];

  public listIdsToReloadTasks: Array<number> = [];

  public undoBoardAnnotations : {[key: string]: any[]} = {};

  public redoBoardAnnotations : {[key: string]: any[]} = {};

  public numberPages : number = 0;

  public presentationSectionSelected : any = {};

  public selectedBoardInfo : {[key: string]: any} = {};

  public homeworkSectionIsSent: {[key: number]: boolean} = {};

  public onlineHomeworks : IOnlineHomework[] = [];

  public isSavingHomework : boolean = false;

  public boardParameters: {[key: string]: any} = {};

  public isBoardActive : boolean = false;

  public isEdupadActive : boolean = false;

  public isSynchronisingEdupad : boolean = false;

  public clientId: string = '';

  public isCursorBoardActive : boolean = true;

  /* Mutations */
  @Mutation
  public init() {
    this.teacherWidgetHomeworks = {
      firstLoad: false,
      personSelected: null,
      courseOpened: null, 
      courses: { page: 0, more: true, content: [] }
    };
    this.teacherWidgetHomeworksSection = {
      firstLoad: false,
      sectionSelected: null,
      courseOpened: null,
      courses: { page: 0, more: true, content: [], pageSize: PAGE_SIZE }
    };
    this.studentWidgetHomeworks = {
      firstLoad: false,
      courses: []
    }
  }

  @Mutation
  public changeConfirmCloseHomeworkViewer(value: boolean) {
    this.confirmCloseHomeworkViewer = value;
  }

  @Mutation
  public setTeacherWidgetHomeworks(data: any) {
    let res: ITeacherWidgetHomework = this.teacherWidgetHomeworks;
    let currentCoursesPages = (res.courses.page + 1);
    res.firstLoad = true;
    res.courses.more = data.totalPages > currentCoursesPages;
    res.courses.page++;
    data.content.forEach((cs: any) => {
      let course = new WidgetTeacherCourse(cs);
      let currentPersonsPages = (course.persons.page + 1);
      course.persons.more = cs.persons.totalPages > currentPersonsPages;
      course.persons.page++;
      cs.persons.content.forEach((us: any) => {
        course.persons.content.push(new WidgetTeacherPerson({
          ...us,
          classId: course.classId,
          courseId: course.id
        }));
      });
      res.courses.content.push(course);
    });
  }

  @Mutation
  public setTeacherWidgetHomeworksSections({data , reload}: {data: any , reload: boolean}) {
    let st: ITeacherWidgetHomeworkSection = this.teacherWidgetHomeworksSection;
    if(reload) {
      st.courses.page = 0;
      st.courses.content = [];
    }
    st.courses.pageSize = st.courses.page == 0 ? data.size : st.courses.pageSize;
    st.courses.more = data.totalPages > st.courses.page + 1;
    st.courses.page++;
    st.firstLoad = true;
    data.content.forEach((cs: any) => {
      let course = new WidgetTeacherCourse(cs);
      course.sections.pageSize = course.sections.page == 0 ? cs.sections.size : course.sections.pageSize;
      course.sections.more = cs.sections.totalPages > (course.sections.page + 1);
      course.sections.page++;
      cs.sections.content.forEach((sec: any) => {
        course.sections.content.push(new WidgetTeacherSectionList({
          ...sec,
          classId: cs.classId,
          courseId: cs.id
        }));
      });
      st.courses.content.push(course);
    });
  }

  @Mutation
  public setStudentsWithHomeworksInCourse({courseId, data}: any) {
    let i = this.teacherWidgetHomeworks.courses.content.findIndex(cs => cs.id == courseId);
    let currentPage = (this.teacherWidgetHomeworks.courses.content[i].persons.page + 1);
    this.teacherWidgetHomeworks.courses.content[i].persons.more = data.totalPages > currentPage;
    this.teacherWidgetHomeworks.courses.content[i].persons.page++;
    data.content.forEach((us: any) => {
      this.teacherWidgetHomeworks.courses.content[i].persons.content.push(new WidgetTeacherPerson({
        ...us,
        classId: this.teacherWidgetHomeworks.courses.content[i].classId,
        courseId: courseId
      }));
    });
  }

  @Mutation
  public updateWidgetTeacherCourse(widgetTeacherCourse: WidgetTeacherCourse) {
    let i = this.teacherWidgetHomeworks.courses.content.findIndex(cs => {
      return cs.id == widgetTeacherCourse.id && cs.classId == widgetTeacherCourse.classId
    });
    Vue.set(this.teacherWidgetHomeworks.courses.content, i, widgetTeacherCourse);
  }

  @Mutation
  public updateWidgetTeacherCourseSection(widgetTeacherCourse: WidgetTeacherCourse) {
    let i = this.teacherWidgetHomeworks.courses.content.findIndex(cs => {
      return cs.id == widgetTeacherCourse.id && cs.classId == widgetTeacherCourse.classId
    });
    Vue.set(this.teacherWidgetHomeworksSection.courses.content, i, widgetTeacherCourse);
  }

  @Mutation
  public updateWidgetTeacherCourseSectionStatus({courseId, classId, sectionId, studentId, status}: any) {
    let courseIndex = this.teacherWidgetHomeworksSection.courses.content
                      .findIndex(course => course.id === courseId && course.classId === classId);
    if(courseIndex === -1){
      return;
    }
    
    let sectionIndex = this.teacherWidgetHomeworksSection.courses.content[courseIndex].persons.content
                        .findIndex(section => section && section.id === sectionId);

    if(sectionIndex === -1){
      return;
    }

    let personIndex = this.teacherWidgetHomeworksSection.courses.content[courseIndex].persons.content[sectionIndex]['users'].content
                      .findIndex(user => user && user.student.id === studentId);

    if(personIndex === -1){
      return;
    }

    this.teacherWidgetHomeworksSection.courses.content[courseIndex].persons.content[sectionIndex]['users'].content[personIndex].section.homeworkStatus = status;
    if(this.teacherWidgetHomeworksSection.courses.content[courseIndex].persons.content[sectionIndex]['users'].content[personIndex].response){
      this.teacherWidgetHomeworksSection.courses.content[courseIndex].persons.content[sectionIndex]['users'].content[personIndex].response.status = status;
    }
  }

  @Mutation
  public openWidgetTeacherCourse(widgetTeacherCourse: WidgetTeacherCourse) {
    let csOpened = this.teacherWidgetHomeworks.courseOpened;
    if(csOpened?.classId == widgetTeacherCourse.classId && csOpened?.id == widgetTeacherCourse.id) {
      this.teacherWidgetHomeworks.courseOpened = null;
    } else {
      this.teacherWidgetHomeworks.courseOpened = widgetTeacherCourse;
    }
  }

  @Mutation
  public openWidgetTeacherCourseSection(widgetTeacherCourse: WidgetTeacherCourse) {
    let csOpened = this.teacherWidgetHomeworksSection.courseOpened;
    if(csOpened?.classId == widgetTeacherCourse.classId && csOpened?.id == widgetTeacherCourse.id) {
      this.teacherWidgetHomeworksSection.courseOpened = null;
    } else {
      this.teacherWidgetHomeworksSection.courseOpened = widgetTeacherCourse;
    }
  }

  @Mutation
  public selectWidgetTeacherPerson(widgetTeacherPerson: WidgetTeacherPerson) {
    Vue.set(this.teacherWidgetHomeworks, 'personSelected', widgetTeacherPerson);
  }

  @Mutation
  public selectWidgetTeacherSection(widgetTeacherSectionList: WidgetTeacherSectionList) {
    Vue.set(this.teacherWidgetHomeworksSection, 'sectionSelected', widgetTeacherSectionList);
  }

  @Mutation
  public setWidgetTeacherPersonSelected({classId, homeworks, student, isReload}: any) {
    let i = this.teacherWidgetHomeworks.courses.content.findIndex(
      cs => cs.id == student.courseId && cs.classId == classId
    );

    let widgetCourse = this.teacherWidgetHomeworks.courses.content[i];
    let k = widgetCourse.persons.content.findIndex(pe => pe.id == student.id);
    let studentHomeworks = isReload ? [] : student.homeworks.content;
    homeworks.content.forEach((h: any) => {
      let homework = h.response ? h.response
                    : h.feedback ? h.feedback                    
                    : h.responseDraft ? h.responseDraft
                    : null;

      let boardGroupHash = h.responseDraft ? h.responseDraft.boardGroup
            : h.feedbackDraft ? h.feedbackDraft.boardGroup
            : null;
      
      studentHomeworks.push(new WidgetTeacherHomework({
        ...homework,
        classId,
        section: h.section,
        courseId: widgetCourse.id,
        boardGroup: boardGroupHash,
        responseDraft: h.responseDraft,
        feedbackDraft: h.feedbackDraft
      }));

      if (boardGroupHash) {
        let boardHomework = this.boardHomeworkHashUsers.find(board => board.boardGroupHash === boardGroupHash);

        if(!boardHomework){
          this.boardHomeworkHashUsers.push({homeworkId: homework.id, boardGroupHash, users: []});
        }
      }
      
    });
    
    student.homeworks.content = studentHomeworks;
    student.homeworks.page++;
    student.homeworks.more = homeworks.totalPages > student.homeworks.page;
    widgetCourse.persons.content.splice(k, 1, student);

    if((isReload && this.teacherWidgetHomeworks.personSelected && this.teacherWidgetHomeworks.personSelected.id === student.id && this.teacherWidgetHomeworks.personSelected.courseId === widgetCourse.id) || !isReload){
      this.teacherWidgetHomeworks.personSelected = student;      
    }
    
  }

  @Mutation
  public setWidgetTeacherSectionSelected({classId, persons, section, isReload}: any) {
    let i = this.teacherWidgetHomeworksSection.courses.content.findIndex(cs => cs.id == section.courseId);
    let widgetCourse = this.teacherWidgetHomeworksSection.courses.content[i];
    let k = widgetCourse.sections.content.findIndex(pe => pe.id == section.id);
    let personsList = isReload ? [] : section.users.content;
    persons.content.forEach((h: any) => {      
      let homeworkBoard = h.responseDraft ? h.responseDraft
                          : h.feedbackDraft ? h.feedbackDraft
                          : null;

      personsList.push(new WidgetTeacherSectionItem(
      {
        ...h,
        courseId: widgetCourse.id,
        sectionListId: section.id,
        classId
      }
      ));

      if(homeworkBoard){
        let boardHomework = this.boardHomeworkHashUsers.find(homework => homework.boardGroupHash === homeworkBoard.boardGroup);

        if(!boardHomework){
          this.boardHomeworkHashUsers.push({homeworkId: homeworkBoard.id, boardGroupHash: homeworkBoard.boardGroup, users: []});
        }
      }
    });
    section.users.content = personsList;
    section.users.page++;
    section.users.more = persons.totalPages > section.users.page;
    widgetCourse.persons.content[k] = section;
    this.teacherWidgetHomeworksSection.sectionSelected = section;
  }

  @Mutation
  public updateWidgetTeacherPerson(
    {student, homework}: {student: WidgetTeacherPerson, homework: WidgetTeacherHomework}) {
    let i = this.teacherWidgetHomeworks.courses.content.findIndex(cs => cs.id == student.courseId);
    let widgetCourse = this.teacherWidgetHomeworks.courses.content[i];
    if(widgetCourse) {
      let k = widgetCourse.persons.content.findIndex(pe => pe.id == student.id);
      let widgetPerson = widgetCourse.persons.content[k];
      if(widgetPerson) {
        widgetPerson.homeworks.content.forEach((h, j) => {
          if(h.id == homework.id) {
            widgetPerson.homeworks.content[j] = homework;
          }
        });
        widgetPerson.homeworkStatus = HomeworksToolsService.getStudentHomeworkStatus(widgetPerson.homeworks.content);
        Vue.set(this.teacherWidgetHomeworks.courses.content, i, widgetCourse);
        let personSelected = this.teacherWidgetHomeworks.personSelected;
        if(personSelected?.id == widgetPerson.id && 
          personSelected.classId == widgetPerson.classId && 
          personSelected.courseId == widgetPerson.courseId) {
            Vue.set(this.teacherWidgetHomeworks, 'personSelected', widgetPerson);
        }
      }
    }
  }

  @Mutation
  public updateResponseWidgetTeacherSection({student, response}: {student: WidgetTeacherPerson, response: WidgetTeacherHomework}) {
    let i = this.teacherWidgetHomeworksSection.courses.content.findIndex(cs => cs.id == student.courseId);
    let widgetCourse = this.teacherWidgetHomeworksSection.courses.content[i];
    let sectionSelected = this.teacherWidgetHomeworksSection.sectionSelected;
    if(widgetCourse) {
      let k = widgetCourse.sections.content.findIndex(sec => {
        let found = sec.users.content.find(secItem => {
          return secItem.response && secItem.response.id == response.id;
        });
        return found ? true : false;
      });
      let widgetSection = widgetCourse.sections.content[k];
      if(widgetSection) { 
        widgetSection.users.content.forEach((h, i) => {
          if(h.response && h.response.id == response.id){
            let type = widgetSection.users.content[i].response.type;
            widgetSection.users.content[i].response.type = type;
            widgetSection.users.content[i].response.grade = response.grade;
            widgetSection.users.content[i].response.status = response.status;
          }
        });
        if(sectionSelected.id == widgetSection.id) {
          Vue.set(this.teacherWidgetHomeworksSection, 'sectionSelected', widgetSection);
        }
      }
    }
  }

  @Mutation
  public setStudentCoursesHomeworks(data: any) {
    data.forEach((cs: any) => {
      this.studentWidgetHomeworks.courses.push(new WidgetStudentCourse(cs));
    });
  }

  @Mutation
  public updateStudentCoursesHomeworks(data:any) {
    this.studentWidgetHomeworks.courses = data.map((cs: any) => new WidgetStudentCourse(cs));
  }

  @Mutation
  public setStudentHomeworksInCourses({widgetCourse, data}: {widgetCourse: WidgetStudentCourse, data: any}) {
    let course: any = this.studentWidgetHomeworks.courses.find(cs => {
      return cs.id === widgetCourse.id && cs.classId === widgetCourse.classId;
    });
    let currentTotalPages = (course.sections.page + 1);
    course.sections.more = data.totalPages > currentTotalPages;
    course.sections.page++;
    course.sections.content = course.sections.content.concat(data.content.map((s:any) => new WidgetTeacherSectionItem({
      ...s,
      classId: course.classId,
      courseId: course.id
    })));
  }

  @Mutation
  public updateCourseHomeworks({widgetCourse, data}: {widgetCourse: WidgetStudentCourse, data: any}) {
    let course: any = this.studentWidgetHomeworks.courses.find(cs => cs.id == widgetCourse.id);
    course.sections.content = data.content.map((s:any) => new WidgetTeacherSectionItem({
      ...s,
      classId: course.classId,
      courseId: course.id
    }));
    course.sections.more = data.totalPages > course.sections.page + 1;
    course.sections.page++;
  }

  @Mutation
  public removeStudentHomework({ courseId, sectionId }:{ courseId:number, sectionId:number }) {
    let course: any = this.studentWidgetHomeworks.courses.find(cs => cs.id == courseId);
    if(course) {
      let index: any = this.studentWidgetHomeworks.courses.indexOf(course);
      let homework: any = this.studentWidgetHomeworks.courses[index].sections.content.find(cs => cs.section.sectionId == sectionId);
      if(homework) {
        let index2: any = this.studentWidgetHomeworks.courses[index].sections.content.indexOf(homework);
        this.studentWidgetHomeworks.courses[index].sections.content.splice(index2,1);
        if(this.studentWidgetHomeworks.courses[index].sections.content.length == 0){
          this.studentWidgetHomeworks.courses.splice(index,1);
        }
      }
    }
  }

  @Mutation
  public setTeacherWidgetTabSelected(tab: TEACHER_WIDGET_TASK_TABS) {
    this.teacherWidgetTabSelected = tab;
  }

  @Mutation
  public setSectionsInCourseHomework({widgetCourse, data}: {widgetCourse: WidgetTeacherCourse, data: any}) {
    let course: any = this.teacherWidgetHomeworksSection.courses.content.find(
      cs => cs.id == widgetCourse.id && cs.classId === widgetCourse.classId
    );

    course.sections.more = data.totalPages > course.sections.page;
    course.sections.page++;
    course.sections.content = course.sections.content.concat(data.content.map((s:any) => new WidgetTeacherSectionList({
      ...s,
      classId: course.classId,
      courseId: course.id
    })));
  }

  @Mutation
  public updateStudentHomeworkDraft({courseId, sectionId, draft}: {courseId: number, sectionId: number, draft: WidgetTeacherHomework}) {
    let iCourse = this.studentWidgetHomeworks.courses.findIndex(cs => cs.id == courseId);
    if(iCourse !== -1) {
      let homework = this.studentWidgetHomeworks.courses[iCourse].sections.content.find(cs => cs.section.sectionId == sectionId);
      if(homework) homework.responseDraft = draft;
    }
  }

  @Mutation
  public setFeedbackDraftInHomework(
    {courseId, studentId, homework}: {courseId: number, studentId: number, homework: WidgetTeacherHomework}) {
      
      /* By students */
      let course = this.teacherWidgetHomeworks.courses.content.find(cs => cs.id == courseId);
      if(course) {
        let student = course.persons.content.find(s => s.id == studentId);
        if(student) {
          let i = student.homeworks.content.findIndex(h => h.id == homework.id);
          if(i !== -1) {
            let homeworkToUpdate : any = {
              ...homework,
              homeworkAssetInfo: student.homeworks.content[i].homeworkAssetInfo,
              section: student.homeworks.content[i].section,
              sectionListId: student.homeworks.content[i].sectionListId,
              courseId: student.homeworks.content[i].courseId,
              classId: student.homeworks.content[i].classId
            };
            student.homeworks.content[i] = homeworkToUpdate;
            this.teacherWidgetHomeworks.personSelected.homeworks.content[i] = homeworkToUpdate;
            Vue.set(this.teacherWidgetHomeworks.personSelected.homeworks.content, i, homeworkToUpdate);
          }
        }
      }
      
      /* By sections */
      let courseSections = this.teacherWidgetHomeworksSection.courses.content.find(cs => cs.id == courseId);
      if(courseSections) {
        let section = courseSections.sections.content.find(s => s.id == homework.section.id);
        if(section) {
          let i = section.users.content.findIndex(u => u.student.id == studentId);
          if(i !== -1) {
            let feedbackDraftToUpdate : any = {
              ...section.users.content[i].feedbackDraft,
              homeworkDraftAssetInfo: homework.homeworkDraftAssetInfo
            }
            section.users.content[i].feedbackDraft = feedbackDraftToUpdate;
            Vue.set(
              this.teacherWidgetHomeworksSection.sectionSelected.users.content[i],
              'feedbackDraft',
              feedbackDraftToUpdate
            );
          }
        }
      }
  }

  @Mutation
  public refreshPersonStatus({student, isReload}) {   
    let course = this.teacherWidgetHomeworks.courses.content.find(cs => cs.id == student.courseId);
    let iCourse = this.teacherWidgetHomeworks.courses.content.indexOf(course);
    if(course) {
      let person = course.persons.content.find(s => s.id == student.id);
      let iPerson = course.persons.content.indexOf(person);
      
      if(person) {
        let status = null;

        person.homeworks.content.forEach(h => {
          switch(h.status) {
            case HOMEWORK_STATUS.NEW:
              status = HOMEWORK_STATUS.NEW;
            break;
            case HOMEWORK_STATUS.SEEN:
              if(status !== HOMEWORK_STATUS.NEW) {
                status = HOMEWORK_STATUS.SEEN;
              }
            break;
            case HOMEWORK_STATUS.FEEDBACK_DRAFT:
              if(status !== HOMEWORK_STATUS.NEW && 
                  status !== HOMEWORK_STATUS.SEEN) {
                status = HOMEWORK_STATUS.FEEDBACK_DRAFT;
              }
            break;
            case HOMEWORK_STATUS.GRADED:
            case HOMEWORK_STATUS.FINISHED:
              if(status !== HOMEWORK_STATUS.NEW &&
                  status !== HOMEWORK_STATUS.SEEN &&
                  status !== HOMEWORK_STATUS.DRAFT &&
                  status !== HOMEWORK_STATUS.FEEDBACK_DRAFT) {
                status = h.status;
              }
            break;
          }
        });

        if(!status && !isReload){
          return;
        }

        student = {...person};
        student.homeworkStatus = status;

        this.teacherWidgetHomeworks.courses.content[iCourse].persons.content.splice(iPerson, 1, student);
      }
    }
  }

  @Mutation
  public setTeacherHomeworkStatus({student, homework}) {
    let courseIndex = this.teacherWidgetHomeworks.courses.content.findIndex(cs => cs.id == student.courseId);

    if(!this.teacherWidgetHomeworks.courses.content[courseIndex]) {
      return
    }

    let personIndex = this.teacherWidgetHomeworks.courses.content[courseIndex].persons.content.findIndex(s => s.id == student.id);
    if(personIndex === -1) {
      return;
    }

    let i = this.teacherWidgetHomeworks.courses.content[courseIndex].persons.content[personIndex].homeworks.content.findIndex(h => h.id == homework.id);

    if(i !== -1) {
      let homeworkToUpdate : any = {
        ...this.teacherWidgetHomeworks.courses.content[courseIndex].persons.content[personIndex].homeworks.content[i],
        status: homework.status
      };

      this.teacherWidgetHomeworks.courses.content[courseIndex].persons.content[personIndex].homeworks.content[i] = homeworkToUpdate;

      if(this.teacherWidgetHomeworks.courseOpened.id === this.teacherWidgetHomeworks.courses.content[courseIndex].id){
        Vue.set(this.teacherWidgetHomeworks.personSelected.homeworks.content, i, homeworkToUpdate);
      }
    }
  }

  @Mutation
  public addBoardHomeworkHashUsers({boardGroupHash, homeworkId, users} : {boardGroupHash: string, homeworkId: number, users: IWhiteboardUser[]}): void {
    if(!boardGroupHash){
      return;
    }

    let boardHomeworkIndex = this.boardHomeworkHashUsers.findIndex(homework => homework.boardGroupHash === boardGroupHash);
    if(boardHomeworkIndex < 0){
      this.boardHomeworkHashUsers.push({homeworkId, boardGroupHash, users});
    }else{
      this.boardHomeworkHashUsers[boardHomeworkIndex].users = users;
    }
  }

  @Mutation
  public addBoardStudentHomeworksHashUsers(widgetCourse: WidgetStudentCourse){
    let course: any = this.studentWidgetHomeworks.courses.find(cs => cs.id == widgetCourse.id);
    course.sections.content.forEach(homework => {
      let homeworkBoard = homework.responseDraft ? homework.responseDraft
                          : homework.feedbackDraft ? homework.feedbackDraft
                          : null;

      if(homeworkBoard && homeworkBoard.boardGroup){
        
        let boardHomework = this.boardHomeworkHashUsers.find(homework => homework.boardGroupHash === homeworkBoard.boardGroup);
        
        if(!boardHomework){
          this.boardHomeworkHashUsers.push({homeworkId: homeworkBoard.id, boardGroupHash: homeworkBoard.boardGroup, users: []});
        }
      }
    });
  }

  @Mutation
  public removeBoardHomeworkHashUsers(boardGroupHash: string){
    if(!boardGroupHash){
      return;
    }
    let homeworkFiltered = this.boardHomeworkHashUsers.filter(homework => homework.boardGroupHash != boardGroupHash);    
    this.boardHomeworkHashUsers = homeworkFiltered;
  }

  @Mutation
  public setListIdsToReload(courseId: number) {
    let exist: boolean = this.listIdsToReloadTasks.includes(courseId);
    if(!exist) this.listIdsToReloadTasks.push(courseId);
  }

  @Mutation
  public reduceListIdsToReload(courseId: number) {
    let index: number = this.listIdsToReloadTasks.findIndex( (id: number) => id === courseId );
    if(index >= 0) this.listIdsToReloadTasks.splice(index , 1);
  }

  @Mutation
  public updateBoardAnnotation(data : any) : void {
    if(!this[data.boardAnnotations][data.boardGroupHash]){
      this[data.boardAnnotations][data.boardGroupHash] = [];
    }

    this[data.boardAnnotations][data.boardGroupHash].push(data.annotatedBoards);
    if (this[data.boardAnnotations][data.boardGroupHash].length > BOARD_CONFIG_PARAMETERS.MAX_UNDO_ACTIONS) {
      this[data.boardAnnotations][data.boardGroupHash].shift();
    }
  }

  @Mutation
  public removeLastBoardActionAnnotation({ boardAnnotations, boardGroupHash }: { boardAnnotations: string, boardGroupHash: string }) {
    if (this[boardAnnotations][boardGroupHash]){
      this[boardAnnotations][boardGroupHash].pop();
    }
  }

  @Mutation
  public clearUndoBoardAnnotations(boardGroupHash : string){
    delete this.undoBoardAnnotations[boardGroupHash];
  }

  @Mutation
  public clearRedoBoardAnnotations(boardGroupHash : string){
    delete this.redoBoardAnnotations[boardGroupHash];
  }

  @Mutation
  public updateNumberPages(numberPages : number) : void {
    this.numberPages = numberPages;
  }

  @Mutation
  public addPresentationSectionContent({ section, courseId, classId }: any) {
    let expiration=false;   
    const nowTest = Moment().format('YYYY-MM-DD');
    if (section.section?.submissionDates) {
      const sectionDate = section.section.submissionDates.lastDate ? section.section.submissionDates.lastDate : section.section.submissionDates.dueDate;
      expiration = sectionDate && Moment(nowTest).isAfter(sectionDate);
    }
    this.presentationSectionSelected = new WidgetTeacherSectionItem(
      {
        ...section,
        expiration:expiration,
        courseId: courseId,
        classId: classId,

      }
    );
  }

  @Mutation
  public clearPresentationSectionContent() {
    this.presentationSectionSelected = {};
  }

  @Mutation
  public updateSelectedBoardInfo(selectedBoardInfo : {[key: string]: any}) {
    this.selectedBoardInfo = selectedBoardInfo;
  }

  @Mutation
  public updateHomeworkSectionIsSent(data : {sectionId: number, isSent: boolean}){
    this.homeworkSectionIsSent = {};
    if(data && data.sectionId){
      this.homeworkSectionIsSent[data.sectionId] = data.isSent;
    }
  }

  @Mutation
  public resetOnlineHomeworkData() {
    this.onlineHomeworks = [];
  }

  @Mutation
  public initOnlineHomeworkData(message: { topicId: number, content: string }) {
    if (!message) {
      return;
    }
    const classId = Number(message.topicId);
    this.onlineHomeworks = this.onlineHomeworks.filter(homework => homework.classId !== classId);
    const content = JSON.parse(message.content);
    for (const sec of content) {
      const onlineHomeworkEntry = {classId, sectionId: sec.sectionId, homeworks: []};
      sec.online.forEach(onlineUser => {
        onlineHomeworkEntry.homeworks.push({
          homeworkId: onlineUser.homeworkId,
          online: [{
            username: onlineUser.username,
            firstName: onlineUser.fname,
            lastName: onlineUser.lname,
            page: onlineUser.page || 1,
            fullName: onlineUser.fname && onlineUser.lname ? `${onlineUser.fname} ${onlineUser.lname}` : ''
          }],
          connected: []
        });
      });
      this.onlineHomeworks.push(onlineHomeworkEntry);
    }
  }

  @Mutation
  public updateOnlineHomeworkData(message: {
    messageType: string, topicId: number, username: string,
    name: string, content: string }) {
    if (!message) {
      return;
    }
    const classId = Number(message.topicId);
    const content = JSON.parse(message.content);
    const sectionId = content.sectionId;
    const homeworkId = content.homeworkId;
    const device = content.device;
    const username = message.username;
    const index = this.onlineHomeworks.findIndex(hw => hw.classId == classId && hw.sectionId == sectionId);
    const entry: IOnlineHomework = index >= 0 ? this.onlineHomeworks[index] : {classId, sectionId, homeworks: []};
    const homework: IOnlineHomeworkDetail = entry?.homeworks?.find(hw => hw.homeworkId == homeworkId);
    if (message.messageType === HOMEWORK_SOCKET_MESSAGES_TYPES.studentConnect
        || message.messageType === HOMEWORK_SOCKET_MESSAGES_TYPES.teacherConnect
        || message.messageType === HOMEWORK_SOCKET_MESSAGES_TYPES.guestConnect) {
      const isStudentConnection = message.messageType === HOMEWORK_SOCKET_MESSAGES_TYPES.studentConnect;
      const page = content.page;
      const newUserEntry: IWhiteboardUser = {
        username, page,
        firstName: content.fname,
        lastName: content.lname,
        fullName: `${content.fname} ${content.lname}`,
        devices: [{device, currentPage: content.page}]
      };
      if (!homework) {
        entry.homeworks.push({
          homeworkId,
          online: isStudentConnection ? [newUserEntry] : [],
          connected: [newUserEntry]
        });
      } else {
        if (isStudentConnection) {
          const onlineUser = homework.online.find(user => user.username == username);
          if (!onlineUser) {
            homework.online.push(newUserEntry);
          }
        }
        const connectedUser = homework.connected.find(user => user.username == username);
        if (connectedUser) {
          connectedUser.devices.push({device, currentPage: content.page});
        } else {
          homework.connected.push(newUserEntry);
        }

      }
      if (index < 0) {
        this.onlineHomeworks.push(entry);
      }
    } else if (message.messageType === HOMEWORK_SOCKET_MESSAGES_TYPES.leave && index >= 0) {
      if (homework) {
        const connectedUser = homework.connected.find(u => u.username == username);
        const onlineUser = homework.online.find(ou => ou.username == username);
        if (connectedUser) {
          connectedUser.devices = connectedUser.devices.filter(d => d.device != device);
          if (connectedUser.devices.length == 0) {
            homework.connected = homework.connected.filter(c => c.username != username);
          }
        }
        if (onlineUser && content.disconnected) {
          homework.online = homework.online.filter(ou => ou.username != username);
        }
      }
      // Cleanup the structure: remove homeworks with no users, and classes with no homeworks
      for (const classEntry of this.onlineHomeworks) {
        classEntry.homeworks = classEntry.homeworks.filter(value => value.online.length > 0 || value.connected.length > 0);
      }
      this.onlineHomeworks = this.onlineHomeworks.filter(value => value.homeworks.length > 0);
    }
  }
  @Mutation
  public updateSavingHomework(isSavingHomework: boolean) {
    this.isSavingHomework = isSavingHomework;
  }
  
  @Mutation
  public updateSavingDraft(data: any){
    if(data.responseDraft){
      let courses = this.studentWidgetHomeworks.courses;
      let course = courses.find(c  => c.classId === data.classId && c.id === data.courseId);
      if(course && course.sections){
        let section = course.sections.content.find(s => s.section.id === data.sectionId);
        if(section && !section.responseDraft){
          section.responseDraft = data.responseDraft;
          section.student = data.student;
        }
      }
    }
  }

  @Mutation
  public setBoardParameters({ wboBlockedTools, wboMaxBoardSize, wboMaxEmitCountPeriod, wboMaxEmitCount, wboMaxPencilPoints }: {
    wboBlockedTools: string, wboMaxBoardSize: number, wboMaxEmitCountPeriod: number, wboMaxEmitCount: number, wboMaxPencilPoints: number
  }) {
    this.boardParameters[BOARD_CONFIG_PARAMETERS.BLOCKED_TOOLS] = wboBlockedTools;
    this.boardParameters[BOARD_CONFIG_PARAMETERS.MAX_BOARD_SIZE] = wboMaxBoardSize;
    this.boardParameters[BOARD_CONFIG_PARAMETERS.MAX_EMIT_COUNT_PERIOD] = wboMaxEmitCountPeriod;
    this.boardParameters[BOARD_CONFIG_PARAMETERS.MAX_EMIT_COUNT] = wboMaxEmitCount;
    this.boardParameters[BOARD_CONFIG_PARAMETERS.MAX_PENCIL_POINTS] = wboMaxPencilPoints;
  }

  @Mutation
  public setBoardActive(isBoardActive : boolean) : void {
    this.isBoardActive = isBoardActive;
  }

  @Mutation
  public setEdupadActive(isEdupadActive : boolean) : void {
    this.isEdupadActive = isEdupadActive;
  }

  @Mutation
  public setSynchronisingEdupad(isSynchronisingEdupad : boolean) : void {
    this.isSynchronisingEdupad = isSynchronisingEdupad;
  }

  @Mutation
  public setCursorBoardActive(isCursorBoardActive : boolean) : void {
    this.isCursorBoardActive = isCursorBoardActive;
  }

  @Mutation
  public updateOnlineHomeworkPage(message: {messageType: string, username: string, content: string }) {
    if (!message || message.messageType != HOMEWORK_SOCKET_MESSAGES_TYPES.changePage) {
      return;
    }
    const username = message.username;
    const content = JSON.parse(message.content);
    const taskId = content.homeworkId;
    const page = content.page;

    for (let classEntry of this.onlineHomeworks) {
      const homework = classEntry.homeworks.find(hw => taskId == hw.homeworkId);
      if (homework) {
        const whiteboardUser = homework.connected.find(user => username == user.username);
        if (whiteboardUser) {
          whiteboardUser.page = page;
        }
      }
    }
  }

  @Mutation
  public updateOnlineHomeworkUsersCollaborating(message: {messageType: 'USERS_ON_TASK', topicId: number, username: string, content: string }) {
    if (!message || message.messageType != HOMEWORK_SOCKET_MESSAGES_TYPES.usersOnTask) {
      return;
    }
    const classId = Number(message.topicId);

    const content = JSON.parse(message.content);
    const sectionId = content.sectionId;
    const homeworkId = content.homeworkId;
    const connected = content.connected || [];

    if (this.onlineHomeworks.length == 0) {
      this.onlineHomeworks.push({classId, sectionId, homeworks: [{homeworkId, online: [], connected: []}]});
    }

    let homeworkFound = false;
    for (let classEntry of this.onlineHomeworks) {
      let homework = classEntry.homeworks.find(hw => homeworkId == hw.homeworkId);
      if (homework) {
        homework.connected = [];
        homework.connected = connected.map(c => ({
          username : c.username,
          firstName: c.fname,
          lastName : c.lname,
          fullName : `${c.fname} ${c.lname}`,
          page     : c.devices[0]?.currentPage || 1,
          devices  : c.devices
        }));
        homeworkFound = true;
      }
    }

    if (!homeworkFound) {
      let newHomework = {homeworkId, online: [], connected: []};
      // Find a top-level entry matching the classId
      let classEntry = this.onlineHomeworks.find(value => value.classId == classId && value.sectionId == sectionId);
      if (!classEntry) {
        classEntry = {classId, sectionId, homeworks: [newHomework]};
        this.onlineHomeworks.push(classEntry);
      }
      classEntry.homeworks.push(newHomework);
      newHomework.connected = connected.map(c => ({
        username : c.username,
        firstName: c.fname,
        lastName : c.lname,
        fullName : `${c.fname} ${c.lname}`,
        page     : c.devices[0]?.currentPage || 1,
        devices  : c.devices
      }));
    }
  }


  @Mutation
  public setClientId(clientId : string) : void {
    this.clientId = clientId;
  }

  /* Actions */
  @Action({})
  public getTeacherWidgetHomeworks(): Promise<void> {
    return new Promise((resolve, reject) => {
      let page = this.teacherWidgetHomeworks.courses.page;
      HomeworksService.getCoursesHomeworksByTeacher(page, PAGE_SIZE).then((res) => {
        this.context.commit(MUTATIONS.SET_TEACHER_WIDGET_HOMEWORKS, res);
        resolve();
      }).catch(err => {
        reject(err);
      });
    }); 
  }

  @Action({})
  public getTeacherWidgetHomeworksBySections(
    { courseId, sectionId, classId , reload = false}): Promise<void> {
    return new Promise((resolve, reject) => {
      let pageSize = this.teacherWidgetHomeworksSection.courses.pageSize;
      let page = reload ? 0 : this.teacherWidgetHomeworksSection.courses.page;
      HomeworksService.getCoursesHomeworksByTeacher(
        page,
        pageSize,
        true,
        classId,
        courseId,
        sectionId).then(
          (res) => {
            this.context.commit(
              MUTATIONS.SET_TEACHER_WIDGET_HOMEWORKS_SECTIONS, {
                data: res,
                reload
              }
            );
            resolve();
          }
      ).catch(err => {
        reject(err);
      });
    });  
  }

  @Action({})
  public getStudentsWithHomeworksFromCourse(course: WidgetTeacherCourse): Promise<void> {
    return new Promise((resolve, reject) => {
      HomeworksService.getStudentsWithHomeworksFromCourse(
        course.classId,
        course.id,
        course.persons.page,
        PAGE_SIZE
      ).then((res) => {
        this.context.commit(MUTATIONS.SET_STUDENTS_WITH_HOMEWORKS_IN_COURSE, {
          courseId: course.id,
          data: res
        });
        resolve();
      }).catch(err => {
        reject(err);
      });
    }); 
  }

  @Action({rawError: true})
  public getTeacherSectionsFromCourseHomework(course: WidgetTeacherCourse): Promise<void> {
    return new Promise((resolve, reject) => {
      HomeworksService.getTeacherSectionsFromCourseHomeworks(
        course.classId,
        course.id,
        course.sections.page,
        PAGE_SIZE
      ).then((res: any) => {
          this.context.commit(MUTATIONS.SET_SECTIONS_IN_COURSE_HOMEWORK, { widgetCourse: course, data: res });
          resolve();
        }
      ).catch(err => {
        reject(err);
      });
    });  
  }

  @Action({})
  public selectWidgetTeacherPersonSelected(student: WidgetTeacherPerson): Promise<void> {
    return new Promise((resolve, reject) => {
      this.context.commit(MUTATIONS.SELECT_WIDGET_TEACHER_PERSON, student);
      HomeworksService.getStudentHomeworksFromTeacherWidget({
        classId: student.classId,
        contentId: student.courseId,
        studentId: student.id,
        page: student.homeworks.page,
        pageSize: PAGE_SIZE
      }).then((res: any) => {
        let isReload = student.homeworks.page == 0 ? true : false;
        this.context.commit(MUTATIONS.SET_WIDGET_TEACHER_PERSON_SELECTED, {classId: student.classId, homeworks: res, student, isReload});
        resolve(res.content.length);
      }).catch(err => {
        reject(err);
      });
    }); 
  }

  @Action({})
  public selectWidgetTeacherSectionSelected({section, isReload} : {section: WidgetTeacherSectionList, isReload: boolean}): Promise<any> {
    return new Promise((resolve, reject) => {
      this.context.commit(MUTATIONS.SELECT_WIDGET_TEACHER_SECTION, section);
      HomeworksService.getSectionsHomeworksFromTeacherWidget({
        classId: section.classId,
        contentId: section.courseId,
        sectionId: section.id,
        page: section.users.page,
        pageSize: PAGE_SIZE,
        includeArchived: false,
        status: null
      }).then((res: any) => {
        this.context.commit(MUTATIONS.SET_WIDGET_TEACHER_SECTION_SELECTED, {classId: section.classId, persons: res, section, isReload});
        resolve(res.content.length);
      }).catch(err => {
        reject(err);
      });
    }); 
  }

  @Action({rawError: true})
  public sendFeedbackToStudent({ req, file, student }: any): Promise<void> {
    return new Promise((resolve, reject) => {
      HomeworksService.updateHomework(new RequestCreateHomework(req), file).then(() => {
        req.status = HOMEWORK_STATUS.GRADED;
        this.context.commit(MUTATIONS.UPDATE_RESPONSE_WIDGET_TEACHER_SECTION, { student, response: req });
        resolve();
      }).catch((err) => {
        reject(err);
      });
    }); 
  }

  @Action({rawError: true})
  public updateTeacherHomeworkStatus({req, student}: {req: WidgetTeacherHomework, student: WidgetTeacherPerson}): Promise<void> {
    return new Promise((resolve, reject) => {
      HomeworksService.updateStatus(req.id, req.status).then(() => {
      this.context.commit(MUTATIONS.SET_TEACHER_HOMEWORK_STATUS, { student, homework: req });
      this.context.commit(MUTATIONS.REFRESH_PERSON_STATUS, {student, isReload: false});
        resolve();
      }).catch((err) => {
        reject(err);
      });
    }); 
  }

  @Action({rawError: true})
  public setTeacherHomeworkGrade({req, student}: {req: WidgetTeacherHomework, student: WidgetTeacherPerson}): Promise<void> {
    return new Promise((resolve, reject) => {
       HomeworksService.setGrade(req.id, req.grade).then(() => {
        req.status = HOMEWORK_STATUS.GRADED;
        this.context.commit(MUTATIONS.UPDATE_WIDGET_TEACHER_PERSON, { student, homework: req });
        this.context.commit(MUTATIONS.UPDATE_RESPONSE_WIDGET_TEACHER_SECTION, { student, response: req });
        this.context.commit(MUTATIONS.REFRESH_PERSON_STATUS, {student, isReload: false});
        resolve();
      }).catch((err) => {
        reject(err);
      });
    }); 
  }

  @Action({rawError: true})
  public getHomeworkScale({homeworkId}: {homeworkId: number}): Promise<any> {
    return new Promise((resolve, reject) => {
       HomeworksService.getHomeworkScale(homeworkId).then((res) => {
        resolve(res);
      }).catch((err) => {
        reject(err);
      });
    }); 
  }

  @Action({rawError: true})
  public getCoursesHomeworksByStudent({refresh} : {refresh: boolean}): Promise<void> {
    return new Promise((resolve, reject) => {
      HomeworksService.getCoursesHomeworksByStudent().then((res) => {
          if( refresh ) {
            this.context.commit(MUTATIONS.UPDATE_STUDENT_COURSES_HOMEWORKS, res);
          } else {
            this.context.commit(MUTATIONS.SET_STUDENT_COURSES_HOMEWORKS, res);
          }
        resolve();
      }).catch(err => {
        reject(err);
      });
    }); 
  }

  @Action({rawError: true})
  public getStudentPendingHomeworks({course, refresh}  : {course: WidgetStudentCourse , refresh?: boolean}): Promise<void> {
    return new Promise((resolve, reject) => {
      HomeworksService.getStudentPendingHomeworks(
        course.classId,
        course.id,
        course.sections.page,
        PAGE_SIZE
      ).then((res) => {
        res.content.map( homework => {
          homework.section.sectionMaterial =  { 
            ...homework.section.sectionMaterial,
            data: new Content({
              ...homework.section.sectionMaterial?.materialAssetInfo, 
              contentFileUrl:homework.section.sectionMaterial?.materialAssetInfo?.url
            }),
          };

          return homework;

        })
        if(refresh) { 
          this.context.commit(MUTATIONS.UPDATE_COURSE_HOMEWORKS, {widgetCourse: course, data: res});
        } else {
          this.context.commit(MUTATIONS.SET_STUDENT_HOMEWORKS_IN_COURSES, 
            {widgetCourse: course, data: res});
        }
        this.context.commit(MUTATIONS.ADD_BOARD_STUDENT_HOMEWORKS_HASH_USERS, course);
        resolve();
      }).catch(err => {
        reject(err);
      });
    }); 
  }

  @Action({rawError: true})
  public setStudentHomeworkResponse({req, file}: any): Promise<void> {
    return new Promise((resolve, reject) => {
      HomeworksService.createHomework(new RequestCreateHomework(req), file).then(() => {
        resolve();
      }).catch((err) => {
        reject(err);
      });
    }); 
  }

  @Action({rawError: true})
  public setHomeworkSolved(homework: WidgetTeacherSectionItem): Promise<void> {
    return new Promise((resolve, reject) => {
      HomeworksService.setHomeworkSolved(homework.section.homeworkId).then(() => {
        this.context.commit(MUTATIONS.REMOVE_STUDENT_HOMEWORK, { 
          courseId: homework.courseId,
          sectionId: homework.section.sectionId
        });
        resolve();
      }).catch((err) => {
        reject(err);
      })
    });
  }

  @Action({rawError: true})
  public setDraftHomework({ homework, file }: {homework: IRequestSetDraftHomework, file: any}) {
    return new Promise((resolve, reject) => {
      HomeworksService.setHomeworkDraft(homework, file).then((res: any) => {
        resolve(res);
      }).catch((err) => {
        reject(err);
      })
    });
  }

  @Action({rawError: true})
  public setFeedbackDraft({ homework, file }: {homework: IRequestSetFeedbacDraft, file: any}) {
    return new Promise((resolve, reject) => {
      HomeworksService.setFeedbackDraft(homework, file).then((res: any) => {
        resolve(res);
      }).catch((err) => {
        reject(err);
      })
    });
  }

 @Action({rawError: true})
 public selectWidgetTeacherPersonSelectedAll({student, isReload} : {student: WidgetTeacherPerson, isReload: boolean}): Promise<any> {
    return new Promise((resolve, reject) => {
      this.context.commit(MUTATIONS.SELECT_WIDGET_TEACHER_PERSON, student);     
      HomeworksService.getStudentHomeworksAllFromTeacherWidget({
        classId: student.classId,
        contentId: student.courseId,
        studentId: student.id,
        page: student.homeworks.page,
        pageSize: PAGE_SIZE
      }).then((res: any) => {
        this.context.commit(
          MUTATIONS.SET_WIDGET_TEACHER_PERSON_SELECTED,
          { classId: student.classId, homeworks: res, student, isReload }
        );
        resolve(res.content.length);
      }).catch(err => {
        reject(err);
      });
    }); 
  }

  @Action({rawError: true})
  public updateWidgetTeacherPersonSelectedAll({student, isStatudReload} : {student: WidgetTeacherPerson, isStatudReload: boolean}): Promise<any> {
     return new Promise((resolve, reject) => {
       HomeworksService.getStudentHomeworksAllFromTeacherWidget({
         classId: student.classId,
         contentId: student.courseId,
         studentId: student.id,
         page: student.homeworks.page,
         pageSize: PAGE_SIZE
       }).then((res: any) => {
         this.context.commit(MUTATIONS.SET_WIDGET_TEACHER_PERSON_SELECTED, {classId: student.classId, homeworks: res, student, isReload: true});
         this.context.commit(MUTATIONS.REFRESH_PERSON_STATUS, {student, isReload: isStatudReload});
         resolve(res.content.length);
       }).catch(err => {
         reject(err);
       });
     }); 
   }

  @Action({rawError: true})
  public fillHomeworkBoardUsers(boardGroupHash: string) : Promise<void>{
    return new Promise((resolve, reject) => {
      HomeworksService.obtaninCurrentWhiteboardUsers(boardGroupHash)
      .then(response => {
          this.context.commit(MUTATIONS.ADD_BOARD_HOMEWORK_HASH_USERS, {boardGroupHash, users: response});
      })
      .catch(error => {
        if(error.response.status === 404){          
          this.context.commit(MUTATIONS.REMOVE_BOARD_HOMEWORK_HASH_USERS, boardGroupHash);
        }else{
          this.context.commit(MUTATIONS.ADD_BOARD_HOMEWORK_HASH_USERS, {boardGroupHash, users: []});
        }     
      }).finally(() => resolve());
    });
  }

  @Action({rawError: true})
  public getHomeworkConverterStatus(homeworkHash: string): Promise<any> {
    return HomeworksService.getHomeworkConverterStatus(homeworkHash);
  }

  @Action({rawError: true})
  public obtainSectionHomework(request : IRequestHomeworkBySectionStudent): Promise<void> {
    return new Promise((resolve, reject) => {
      HomeworksService.getHomeworkBySectionStudent(request)
      .then((res: any) => {
        this.context.commit(MUTATIONS.ADD_PRESENTATION_SECTION_CONTENT, {section: res, courseId: request.contentId, classId: request.classId});
        resolve();
      }).catch(err => {
        reject(err);
      });
    }); 
  }
  
  @Action({rawError: true})
  public obtainStudentsHomeworkStatus(request : IRequestHomeworkStatusByStudent): Promise<HomeworkStatusStudent[]> {
    return new Promise((resolve, reject) => {
      HomeworksService.getStudentsHomeworkStatus(request)
      .then((res: any) => {
        resolve(res);
      }).catch(err => {
        reject(err);
      });
    }); 
  }

  /* Initial State */
  @Mutation
  public initState(state) {
    this.teacherWidgetTabSelected = TEACHER_WIDGET_TASK_TABS.BY_STUDENT;
    this.teacherWidgetHomeworks  = {
      firstLoad: false,
      personSelected: null,
      courseOpened: null, 
      courses: { page: 0, more: true, content: [] }
    };
    this.teacherWidgetHomeworksSection  = {
      firstLoad: false,
      sectionSelected: null,
      courseOpened: null,
      courses: { page: 0, more: true, content: [], pageSize: PAGE_SIZE }
    };
    this.studentWidgetHomeworks  = {
      firstLoad: false, courses: []
    }
    this.confirmCloseHomeworkViewer  = false;
    this.boardHomeworkHashUsers = [];
    this.listIdsToReloadTasks = [];
    this.undoBoardAnnotations = {};
    this.redoBoardAnnotations = {};
    this.numberPages = 0;
    this.presentationSectionSelected = {};
    this.selectedBoardInfo = {};
    this.homeworkSectionIsSent = {};
    this.onlineHomeworks = [];
    this.isSavingHomework = false;
    this.boardParameters = {};
    this.isBoardActive = false;
    this.isEdupadActive = false;
    this.clientId = '';
  }

}